// GET and PATCH /api/partner/me, POST /api/partner/registration, GET /api/partner/referrals[/:id]
// and POST /api/partner/referrals (apps/server/src/partners.mjs): who is signed in, and
// their partner record, or null before they apply; a verified partner's change to their
// own phone and website (MOTHER-182); the application that makes one; a
// verified partner's own leads; and the referral that adds one. The session token only
// ever goes to this page's own /api/partner/ routes.
import type { ReferralBody } from './referral';
import type { RegistrationValues } from './registration';

/** Who at Olytic looks after a verified partner: a name (may be null) and an email. */
export interface OlyticContact {
  name: string | null;
  email: string;
}

/** The partner record /api/partner/me and a registration return. */
export interface PartnerRecord {
  id: string;
  /** pending, verified, declined or deactivated. */
  status: string;
  firstName: string | null;
  lastName: string | null;
  companyName: string | null;
  /** Verified only (MOTHER-87): the day their company became a partner, `YYYY-MM-DD`, or null. */
  partnerSince?: string | null;
  /** Verified only (MOTHER-87): their referral rate, a percent (10 means 10%), the one money value they see. */
  referralRate?: number | null;
  /** Verified only (MOTHER-87): their contact at Olytic, or null before one is assigned. */
  olyticContact?: OlyticContact | null;
  /** Verified only (MOTHER-181): the partner's own phone and website, which they may change. */
  phone?: string | null;
  companyWebsite?: string | null;
}

/** A PATCH /api/partner/me body: only the fields that changed, trimmed; null clears one. */
export type ProfileChange = Partial<Record<'phone' | 'companyWebsite', string | null>>;

/** One stage a lead has reached, and when (an ISO timestamp). */
export interface ReferralStage {
  stage: string;
  at: string;
}

/** A lead the partner referred, as /api/partner/referrals answers it: what they typed, its stage and Olytic's latest update. */
export interface Referral {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  title: string | null;
  companyName: string | null;
  companyWebsite: string | null;
  relationship: string | null;
  need: string | null;
  timeline: string | null;
  /** submitted, contacted, in_discussions, closed_won, closed_lost or not_a_fit. */
  stage: string;
  stageChangedAt: string | null;
  createdAt: string;
  update: { text: string; at: string | null } | null;
  stages: ReferralStage[];
}

export interface PartnerMe {
  email: string;
  name: string | null;
  partner: PartnerRecord | null;
}

/** A failed /api/partner/* request: its HTTP status, the server's message and, on a 400, its message per field. */
export class PartnerApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly fields: Record<string, string> = {},
  ) {
    super(message);
    this.name = 'PartnerApiError';
  }
}

type Fetch = (input: string, init: RequestInit) => Promise<Response>;

/**
 * Asks the server who this is, with a fresh session token. No token is a 401, as the
 * server would answer; a network failure rejects with the fetch's own error.
 */
export async function fetchPartnerMe(getToken: () => Promise<string | null>, fetchImpl: Fetch = fetch): Promise<PartnerMe> {
  const token = await getToken();
  if (token === null) throw new PartnerApiError(401, 'Your session has ended.');
  const response = await fetchImpl('/api/partner/me', { headers: { Authorization: `Bearer ${token}` } });
  return meOf(response, await responsePayload(response));
}

/**
 * Changes the verified partner's own phone and website (MOTHER-181): one PATCH with only
 * the changed keys, and who they now are on a 200. A 400 carries the server's message per
 * field; a 403 means the partner is not (or no longer) verified; no token is a 401, as
 * the server would answer; a network failure rejects with the fetch's own error.
 */
export async function updatePartnerMe(getToken: () => Promise<string | null>, change: ProfileChange, fetchImpl: Fetch = fetch): Promise<PartnerMe> {
  const token = await getToken();
  if (token === null) throw new PartnerApiError(401, 'Your session has ended.');
  const response = await fetchImpl('/api/partner/me', {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(change),
  });
  return meOf(response, await responsePayload(response));
}

/** /api/partner/me's answer, to a GET or a PATCH: who this is, or a PartnerApiError. */
function meOf(response: Response, payload: unknown): PartnerMe {
  if (!response.ok) throw apiError(response.status, payload);
  if (!isRecord(payload) || typeof payload.email !== 'string') {
    throw new PartnerApiError(response.status, 'The profile response was invalid.');
  }
  return {
    email: payload.email,
    name: typeof payload.name === 'string' ? payload.name : null,
    partner: partnerOf(payload.partner),
  };
}

/**
 * Applies: one POST with the seven fields, and the new (pending) record on a 201. A 400
 * carries the server's message per field; no token is a 401, as the server would answer;
 * a network failure rejects with the fetch's own error.
 */
export async function submitRegistration(
  getToken: () => Promise<string | null>,
  body: RegistrationValues,
  fetchImpl: Fetch = fetch,
): Promise<PartnerRecord> {
  const token = await getToken();
  if (token === null) throw new PartnerApiError(401, 'Your session has ended.');
  const response = await fetchImpl('/api/partner/registration', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const payload = await responsePayload(response);
  if (!response.ok) throw apiError(response.status, payload);
  const partner = isRecord(payload) ? partnerOf(payload.partner) : null;
  if (partner === null) throw new PartnerApiError(response.status, 'The registration response was invalid.');
  return partner;
}

/** A GET of one of this page's own routes with a fresh session token: the JSON on a 2xx, else a PartnerApiError. */
async function getJson(getToken: () => Promise<string | null>, path: string, fetchImpl: Fetch): Promise<unknown> {
  const token = await getToken();
  if (token === null) throw new PartnerApiError(401, 'Your session has ended.');
  const response = await fetchImpl(path, { headers: { Authorization: `Bearer ${token}` } });
  const payload = await responsePayload(response);
  if (!response.ok) throw apiError(response.status, payload);
  return payload;
}

/**
 * The verified partner's own leads, newest first. A 403 means the partner is not (or no
 * longer) verified; no token is a 401, as the server would answer; a network failure
 * rejects with the fetch's own error.
 */
export async function fetchReferrals(getToken: () => Promise<string | null>, fetchImpl: Fetch = fetch): Promise<Referral[]> {
  const payload = await getJson(getToken, '/api/partner/referrals', fetchImpl);
  const list = isRecord(payload) && Array.isArray(payload.referrals) ? payload.referrals.map(referralOf) : null;
  if (list === null || list.some((referral) => referral === null)) throw new PartnerApiError(200, 'The leads response was invalid.');
  return list as Referral[];
}

/** One of the partner's leads. Another partner's id, or a made-up one, is a 404. */
export async function fetchReferral(getToken: () => Promise<string | null>, id: string, fetchImpl: Fetch = fetch): Promise<Referral> {
  const payload = await getJson(getToken, `/api/partner/referrals/${encodeURIComponent(id)}`, fetchImpl);
  const referral = isRecord(payload) ? referralOf(payload.referral) : null;
  if (referral === null) throw new PartnerApiError(200, 'The lead response was invalid.');
  return referral;
}

/**
 * Refers a company: one POST with the ten fields and `consent: true`, and the new lead on
 * a 201. A 400 carries the server's message per field; a 403 means the partner is not (or
 * no longer) verified; no token is a 401, as the server would answer; a network failure
 * rejects with the fetch's own error.
 */
export async function submitReferral(getToken: () => Promise<string | null>, body: ReferralBody, fetchImpl: Fetch = fetch): Promise<Referral> {
  const token = await getToken();
  if (token === null) throw new PartnerApiError(401, 'Your session has ended.');
  const response = await fetchImpl('/api/partner/referrals', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const payload = await responsePayload(response);
  if (!response.ok) throw apiError(response.status, payload);
  const referral = isRecord(payload) ? referralOf(payload.referral) : null;
  if (referral === null) throw new PartnerApiError(response.status, 'The referral response was invalid.');
  return referral;
}

function referralOf(value: unknown): Referral | null {
  if (!isRecord(value) || typeof value.id !== 'string' || typeof value.stage !== 'string' || typeof value.createdAt !== 'string') return null;
  const update = isRecord(value.update) && typeof value.update.text === 'string' ? { text: value.update.text, at: textOrNull(value.update.at) } : null;
  const stages = Array.isArray(value.stages)
    ? value.stages.filter((s): s is ReferralStage => isRecord(s) && typeof s.stage === 'string' && typeof s.at === 'string').map((s) => ({ stage: s.stage, at: s.at }))
    : [];
  return {
    id: value.id,
    firstName: textOrNull(value.firstName),
    lastName: textOrNull(value.lastName),
    email: textOrNull(value.email),
    phone: textOrNull(value.phone),
    title: textOrNull(value.title),
    companyName: textOrNull(value.companyName),
    companyWebsite: textOrNull(value.companyWebsite),
    relationship: textOrNull(value.relationship),
    need: textOrNull(value.need),
    timeline: textOrNull(value.timeline),
    stage: value.stage,
    stageChangedAt: textOrNull(value.stageChangedAt),
    createdAt: value.createdAt,
    update,
    stages,
  };
}

function apiError(status: number, payload: unknown): PartnerApiError {
  const message = isRecord(payload) && typeof payload.error === 'string' && payload.error.length > 0 ? payload.error : 'The partner portal could not answer.';
  const fields: Record<string, string> = {};
  if (isRecord(payload) && isRecord(payload.fields)) {
    for (const [key, value] of Object.entries(payload.fields)) if (typeof value === 'string') fields[key] = value;
  }
  return new PartnerApiError(status, message, fields);
}

function partnerOf(value: unknown): PartnerRecord | null {
  if (!isRecord(value) || typeof value.id !== 'string' || typeof value.status !== 'string') return null;
  const record: PartnerRecord = {
    id: value.id,
    status: value.status,
    firstName: textOrNull(value.firstName),
    lastName: textOrNull(value.lastName),
    companyName: textOrNull(value.companyName),
  };
  // The verified partner's standing: only keys the server sent, so other states stay without them.
  if ('partnerSince' in value) record.partnerSince = textOrNull(value.partnerSince);
  if ('referralRate' in value) record.referralRate = typeof value.referralRate === 'number' && Number.isFinite(value.referralRate) ? value.referralRate : null;
  if ('olyticContact' in value) {
    const contact = value.olyticContact;
    record.olyticContact = isRecord(contact) && typeof contact.email === 'string' ? { name: textOrNull(contact.name), email: contact.email } : null;
  }
  if ('phone' in value) record.phone = textOrNull(value.phone);
  if ('companyWebsite' in value) record.companyWebsite = textOrNull(value.companyWebsite);
  return record;
}

function textOrNull(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

async function responsePayload(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return undefined;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
